import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/axios";
import toast from "react-hot-toast";
import { FaArrowLeft, FaUsers, FaBalanceScale } from "react-icons/fa";

export default function DealerDetails() {
  const { dealerId } = useParams();
  const navigate = useNavigate();

  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [minSales, setMinSales] = useState("");

  useEffect(() => {
    const fetchDealer = async () => {
      try {
        const res = await api.get("/admin/all-dealers");

        if (!res.data?.success) {
          // toast.error("Failed to load dealers");
          navigate("/admin-dashboard");
          return;
        }

        // ✅ backend sends `id`, not `_id`
        const found = res.data.dealers.find(
          (d) => String(d.id) === String(dealerId)
        );

        if (!found) {
          toast.error("Dealer not found");
          navigate("/admin-dashboard");
          return;
        }

        setDealer(found);
      } catch (error) {
        console.error(error);
        // toast.error("Failed to load dealer");
        navigate("/admin-dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchDealer();
  }, [dealerId, navigate]);


  // 🔥 SALES CALCULATION
  const getUserTotalSales = (user) => {
    if (!Array.isArray(user.sales)) return 0;
    return user.sales.reduce(
      (sum, sale) => sum + Number(sale.quantity || 0),
      0
    );
  };

  const dealerTotalSales = useMemo(() => {
    if (!dealer?.users) return 0;
    return dealer.users.reduce(
      (sum, u) => sum + getUserTotalSales(u),
      0
    );
  }, [dealer?.users]);

  const filteredUsers = useMemo(() => {
    if (!dealer?.users) return [];
    const q = search.toLowerCase();

    return dealer.users.filter((u) => {
      const totalSales = getUserTotalSales(u);

      return (
        (!search ||
          u.name?.toLowerCase().includes(q) ||
          u.mobile?.includes(q) ||
          u.district?.toLowerCase().includes(q) ||
          u.area?.toLowerCase().includes(q)) &&
        (!typeFilter ||
          u.type?.toLowerCase() === typeFilter.toLowerCase()) &&
        (!minSales || totalSales >= Number(minSales))
      );
    });
  }, [dealer?.users, search, typeFilter, minSales]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading…
      </div>
    );
  }

  if (!dealer) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-6">
        <button
          onClick={() => navigate("/admin-dashboard")}
          className="flex items-center gap-2 text-yellow-600 mb-4"
        >
          <FaArrowLeft /> Back
        </button>

        <h1 className="text-3xl font-bold">{dealer.partyName}</h1>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          <StatCard
            icon={<FaUsers size={28} />}
            label="Total Customers"
            value={dealer.users.length}
          />
          <StatCard
            icon={<FaBalanceScale size={28} />}
            label="Total Sales (KG)"
            value={dealerTotalSales.toLocaleString()}
          />
        </div>

        {/* FILTERS */}
        <div className="bg-white p-6 rounded-xl shadow mb-6 grid grid-cols-1 sm:grid-cols-4 gap-4">
          <input
            className="input"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="input"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            <option>Kadiya</option>
            <option>Mason</option>
            <option>Contractor</option>
            <option>Architect</option>
            <option>Builder</option>
            <option>Civil Engineer / Consultant</option>
            <option>Fabricator</option>
            <option>End User (Individual House)</option>

          </select>
          <input
            type="number"
            className="input"
            placeholder="Min Sales (KG)"
            value={minSales}
            onChange={(e) => setMinSales(e.target.value)}
          />
          <button
            onClick={() => {
              setSearch("");
              setTypeFilter("");
              setMinSales("");
            }}
            className="bg-red-500 text-white rounded-xl"
          >
            Reset
          </button>
        </div>

        {/* TABLE */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full min-w-[900px]">
            <thead className="bg-gray-100">
              <tr>
                <Th>Name</Th>
                <Th>Mobile</Th>
                <Th>Type</Th>
                <Th>Area</Th>
                <Th center>Sales (KG)</Th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredUsers.map((u) => (
                <tr key={u._id}>
                  <td className="px-6 py-3">{u.name}</td>
                  <td className="px-6 py-3">{u.mobile}</td>
                  <td className="px-6 py-3">{u.type}</td>
                  <td className="px-6 py-3">{u.area}</td>
                  <td className="px-6 py-3 text-center font-bold text-yellow-700">
                    {getUserTotalSales(u).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ icon, label, value }) => (
  <div className="bg-white p-6 rounded-xl shadow flex gap-4">
    <div className="p-4 bg-yellow-600 text-white rounded-xl">{icon}</div>
    <div>
      <p className="text-gray-500">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  </div>
);

const Th = ({ children, center }) => (
  <th
    className={`px-6 py-3 text-sm font-semibold ${center ? "text-center" : "text-left"
      }`}
  >
    {children}
  </th>
);
